import {useMemo} from 'react';

import {ProcedureEdgeNode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ExperimentMethod} from 'studies/typed/shared/procedure-runs/design-state/ExperimentalInputs';
import {
  ConditionGroup,
  isPersistedCondition,
} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';

import {GetStudyDesignFromProcedureRunGraphQL as ProcedureRunNode} from '../../procedure-runs/design-state/__generated/GetStudyDesignFromProcedureRunGraphQL';
import {
  getConditionReplicateEdges,
  getMethodExecutionInstanceInfoForPlacements,
} from '../../procedure-runs/design-state/getStudyDesignFromProcedureRun';
import {
  buildPlanConditionReplicateToNodeIdMap,
  buildPmeiToNodeIdMap,
  executionEdgeSpecsFromConditionGraph,
  FlowchartEdgeSpec,
  methodLinksFromProcedureEdges,
  edgeSpecsFromMethodLinks,
  PlanConditionReplicateEdge,
  planEdgeSpecsFromConditionGraph,
} from './ProcedureRunDesignFlowchartLayoutUtils';

interface BaseEdgeSpecsInputs {
  isFlowchartProcedureRunDesignEnabled: boolean;
  conditions: Array<ConditionGroup>;
  methods: Array<ExperimentMethod>;
}

/**
 * Execution-time inputs: a promoted `ProcedureRun` has a per-run condition graph, the source of truth
 * for the actual execution structure (split/pool can diverge from the template). `procedureEdges`
 * remains optional so draft (newly added) conditions can still render template-method fan-out edges.
 */
interface ExecutionInputs {
  procedureRun: ProcedureRunNode;
  activeMethodId: string | null;
  procedureEdges?: Array<ProcedureEdgeNode>;
  isPlanningTimeSplitPoolEnabled?: never;
  planConditionReplicateFlowchart?: never;
}

/**
 * Planning-time inputs. With planning-time split/pool enabled, edges come from the plan's persisted
 * condition graph (`planConditionReplicateFlowchart`) and are editable. Otherwise the template
 * procedure method links fan out read-only across each condition/replicate row.
 */
interface BaseNonExecutionInputs {
  procedureRun?: never;
  activeMethodId?: never;
  procedureEdges: Array<ProcedureEdgeNode>;
}

interface SplitPoolPlanInputs extends BaseNonExecutionInputs {
  isPlanningTimeSplitPoolEnabled: true;
  planConditionReplicateFlowchart: Array<PlanConditionReplicateEdge>;
}

interface TemplateFanOutPlanInputs extends BaseNonExecutionInputs {
  isPlanningTimeSplitPoolEnabled: false;
  planConditionReplicateFlowchart?: never;
}

type NonExecutionInputs = SplitPoolPlanInputs | TemplateFanOutPlanInputs;

export type UseProcedureRunDesignFlowchartEdgeSpecsProps = BaseEdgeSpecsInputs &
  (ExecutionInputs | NonExecutionInputs);

// Stable default so the execution path (which omits `procedureEdges`) keeps a referentially stable
// `useMemo` dependency instead of allocating a fresh array each render.
const EMPTY_PROCEDURE_EDGES: Array<ProcedureEdgeNode> = [];

/**
 * Derives the design flowchart's edge specs for both the run and plan tabs. When a promoted
 * `procedureRun` is supplied, edges come from its runtime condition graph (resolving PMEIs to grid
 * nodes, marking edges past the active unit-op layer `readonly`). Otherwise the template method
 * graph is fanned out per condition/replicate row for planning and tagged `readonly`.
 * Plans with planning-time split/pool enabled use condition-graph edges that retain the default editable variant.
 * Returns no edges when the flowchart UI is disabled, since only it consumes them.
 */
export function useProcedureRunDesignFlowchartEdgeSpecs(
  props: UseProcedureRunDesignFlowchartEdgeSpecsProps
): Array<FlowchartEdgeSpec> {
  const {
    isFlowchartProcedureRunDesignEnabled,
    conditions,
    methods,
    procedureRun,
    activeMethodId,
    isPlanningTimeSplitPoolEnabled,
    planConditionReplicateFlowchart,
  } = props;
  const procedureEdges = props.procedureEdges ?? EMPTY_PROCEDURE_EDGES;
  return useMemo(() => {
    if (!isFlowchartProcedureRunDesignEnabled) {
      return [];
    }
    if (procedureRun) {
      const methodIndexById = new Map(methods.map((method, index) => [method.id, index]));
      const activeMethodIndex = activeMethodId == null ? -1 : (methodIndexById.get(activeMethodId) ?? -1);
      const executionEdgeSpecs = executionEdgeSpecsFromConditionGraph(
        getConditionReplicateEdges(procedureRun),
        // Archived PMEIs are kept so archived edges resolve to a node id and can render when "Show
        // archived" is on; without them, executionEdgeSpecsFromConditionGraph skips those edges
        // entirely when their source/target PMEIs are missing from the map. Emission is never gated on the
        // toggle: visibility is decided at render, and the save payload and connectivity validation filter
        // to live edges downstream (excludeArchivedEdgeSpecs), so the toggle changes only what is
        // shown, never what is saved or whether the design validates. By design, saving only uses live edges.
        buildPmeiToNodeIdMap(
          getMethodExecutionInstanceInfoForPlacements(procedureRun, {includeArchived: true}),
          conditions
        ),
        {methods, activeMethodId}
      );
      const draftConditionEdgeSpecs = edgeSpecsFromMethodLinks({
        conditionGroups: conditions,
        methods,
        links: methodLinksFromProcedureEdges(procedureEdges),
        shouldIncludeCondition: (condition) => !isPersistedCondition(condition),
        getVariantForLink: (link) => {
          if (activeMethodIndex < 0) {
            return 'readonly';
          }
          const sourceMethodIndex = methodIndexById.get(link.sourceMethodId);
          return sourceMethodIndex != null && sourceMethodIndex >= activeMethodIndex
            ? 'readonly'
            : 'editable';
        },
      });
      return [...executionEdgeSpecs, ...draftConditionEdgeSpecs];
    }
    if (isPlanningTimeSplitPoolEnabled) {
      return planEdgeSpecsFromConditionGraph(
        planConditionReplicateFlowchart,
        buildPlanConditionReplicateToNodeIdMap(conditions, methods)
      );
    }
    return edgeSpecsFromMethodLinks({
      conditionGroups: conditions,
      methods,
      links: methodLinksFromProcedureEdges(procedureEdges),
      getVariantForLink: () => 'readonly',
    });
  }, [
    isFlowchartProcedureRunDesignEnabled,
    conditions,
    methods,
    procedureRun,
    activeMethodId,
    procedureEdges,
    isPlanningTimeSplitPoolEnabled,
    planConditionReplicateFlowchart,
  ]);
}
